import React, {
  type ComponentType,
  PureComponent,
  type ReactNode,
} from 'react';

import classNames from 'classnames';
import { uniqueId } from 'lodash-es';

import type { ObjectType } from '@iconik-io/iconik-api/src/types/object_types';
import type { ColumnWidthDefinition } from '@iconik-io/shared-lib/src/constants/search';
import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';
import type {
  SortObject,
  SortOrder,
} from '@iconik-io/shared-lib/src/utils/sort/types';

import ErrorBoundary from '../../components/error_boundary/error_boundary';
import InterObserver from '../../components/inter_observer/inter_observer';
import type { AnyObject } from '../../types/common';
import type { SearchItemKeyframes } from '../../types/search_item_keyframes';
import { ui } from '../../utils/helpers';
import LoadingBar from '../loading_bar/loading_bar';
import ColumnDragLayer from './column_drag_layer/column_drag_layer';
import CustomHead from './custom_head/custom_head';
import Head from './head/head';
import TableRow, { type TableRowProps } from './table_row';
import type { Column, DragAndDropVSide } from './types';

import styles from './table.module.css';

export const MIN_COLUMN_WIDTH = '1e-5%';

type Props<Item, RowComponentProps> = {
  renderColumns: Column<Item>[];
  selectedIds: string[];
  draggable: boolean;
  fixedTableHeader: boolean;
  resizableColumns: boolean;
  draggableColumns?: boolean;
  uiKey?: string;
  showHeader: boolean;
  // Class for the <table> element.
  className?: string;
  // Class for the <td> element.
  cellClassName?: string;
  // Class for the wrapper of the cell content element.
  cellContentClassName?: string;
  tbodyClassName?: string;
  tableRowComponent: ComponentType<RowComponentProps & TableRowProps<Item>>;
  tableRowComponentProps: RowComponentProps;
  onBottomVisible?: () => void;
  observeWhenEmpty?: boolean;
  items: Item[];
  dropTargetId?: string;
  disabledIds: string[];
  fixedLayout?: boolean;
  isLoading: boolean;
  dateFormat?: string;
  dateTimeFormat?: string;
  onSort?: (name: string, order: SortOrder | undefined) => void;
  sorting?: SortObject;
  onColumnsResized?: (columns: ColumnWidthDefinition[]) => void;
  onColumnDragStart?: () => void;
  onColumnDragEnd?: () => void;
  onColumnReordered?: (fromIndex: number, toIndex: number) => void;
  getObjectLink?: (
    id: string,
    objectType: ObjectType,
    versionId?: string,
  ) => string;
  onTitleLinkClick?: (item: Item, event: React.MouseEvent) => void;
  titleWidth?: number;
  isTitleAutoResize?: boolean;
  keyframesUpdate?: SearchItemKeyframes;
  dragAndDropVSide?: DragAndDropVSide;
  flashHighlightIds?: string[];
  aria?: AriaProps;
};

type State = {
  width: number | null;
};

class TableView<
  Item extends AnyObject,
  RowComponentProps,
> extends PureComponent<Props<Item, RowComponentProps>, State> {
  static defaultProps = {
    selectedIds: [],
    disabledIds: [],
    draggable: false,
    fixedTableHeader: true,
    resizableColumns: false,
    draggableColumns: false,
    showHeader: true,
    tableRowComponent: TableRow,
    tableRowComponentProps: undefined,
    isLoading: false,
  };

  rootRef = React.createRef<HTMLDivElement>();

  tableRef = React.createRef<HTMLTableElement>();

  resizeObserver: ResizeObserver | null = null;

  resizeObserverTable: ResizeObserver | null = null;

  interObserverKeyPrefix = uniqueId('table_observer_');

  state: State = {
    width: null,
  };

  componentDidMount(): void {
    if (this.props.resizableColumns) {
      this.resizeObserver = new ResizeObserver((entries) => {
        if (!this.tableRef.current) {
          return;
        }
        const [entry] = entries;
        const { width } = entry.contentRect;
        this.setState({ width });
        this.tableRef.current.style.width = `${width}px`;
      });
      if (!this.rootRef.current) {
        return;
      }
      this.resizeObserver.observe(this.rootRef.current);

      this.resizeObserverTable = new ResizeObserver((entries) => {
        if (!this.rootRef.current) {
          return;
        }
        const [entry] = entries;
        const { height } = entry.contentRect;
        this.rootRef.current.style.height = `${height}px`;
      });
      if (!this.tableRef.current) {
        return;
      }
      this.resizeObserverTable.observe(this.tableRef.current);
    }
  }

  componentWillUnmount(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeObserverTable) {
      this.resizeObserverTable.disconnect();
    }
  }

  handleColumnsUpdated = (columns: ColumnWidthDefinition[]): void => {
    if (!this.rootRef.current) {
      return;
    }
    let minWidth = 0;
    columns.forEach((column) => {
      minWidth += column.min ?? 0;
    });
    const containerWidth = this.rootRef.current.parentElement?.clientWidth ?? 0;
    this.rootRef.current.style.minWidth =
      minWidth <= containerWidth ? `${minWidth}px` : '';
  };

  render(): ReactNode {
    const {
      items,
      renderColumns,
      dateFormat,
      dateTimeFormat,
      selectedIds,
      disabledIds,
      dropTargetId,
      uiKey,
      fixedTableHeader,
      fixedLayout,
      keyframesUpdate,
      resizableColumns,
      sorting,
      onSort,
      showHeader,
      className,
      cellClassName,
      cellContentClassName,
      tbodyClassName,
      tableRowComponent: RowComponent,
      tableRowComponentProps,
      isLoading,
      draggable,
      draggableColumns,
      onBottomVisible,
      getObjectLink,
      onTitleLinkClick,
      onColumnsResized,
      onColumnDragStart,
      onColumnReordered,
      onColumnDragEnd,
      dragAndDropVSide,
      flashHighlightIds,
      aria,

      titleWidth,
      isTitleAutoResize,
    } = this.props;

    if (!items || !items.length) {
      return isLoading ? (
        <LoadingBar />
      ) : this.props.observeWhenEmpty && onBottomVisible ? (
        <InterObserver
          key={`${this.interObserverKeyPrefix}_${items.length}`}
          className={styles.inter_observer}
          onIntersect={onBottomVisible}
        />
      ) : null;
    }

    const customHead = resizableColumns || draggableColumns;
    const attrs = uiKey ? ui({ table: uiKey }) : {};

    return (
      <div
        className={classNames(styles.root, {
          [styles.custom_head]: customHead,
        })}
        ref={this.rootRef}
      >
        <div className={styles.wrapper}>
          <ColumnDragLayer />
          <table
            {...attrs}
            ref={this.tableRef}
            className={classNames(
              styles.table,
              className,
              { [styles.header_fixed]: fixedTableHeader },
              { [styles.fixed_layout]: fixedLayout },
              { [styles.title_auto_resize]: isTitleAutoResize },
            )}
            role="grid"
            {...ariaToReactProps(aria)}
          >
            {customHead && (
              <CustomHead
                renderColumns={renderColumns}
                sorting={sorting}
                onSort={onSort}
                onColumnsUpdated={this.handleColumnsUpdated}
                onColumnsResized={onColumnsResized}
                onColumnDragStart={onColumnDragStart}
                onColumnReordered={onColumnReordered}
                onColumnDragEnd={onColumnDragEnd}
                draggableColumns={draggableColumns}
                width={this.state.width}
                collapsed={!showHeader}
                titleWidth={titleWidth}
                isTitleAutoResize={isTitleAutoResize}
              />
            )}
            {showHeader && !customHead && (
              <Head
                renderColumns={renderColumns}
                sorting={sorting}
                onSort={onSort}
              />
            )}
            <tbody className={tbodyClassName}>
              {items.map((item, index) => (
                <ErrorBoundary key={item.id ?? index}>
                  <RowComponent
                    {...tableRowComponentProps}
                    item={item}
                    cellClassName={cellClassName}
                    cellContentClassName={cellContentClassName}
                    renderColumns={renderColumns}
                    isSelected={item.id ? selectedIds.includes(item.id) : false}
                    isDisabled={item.id ? disabledIds.includes(item.id) : false}
                    className={
                      flashHighlightIds?.includes(item.id)
                        ? styles.tr_flash_highlight
                        : undefined
                    }
                    isDropTarget={dropTargetId === item.id}
                    dateFormat={dateFormat}
                    dateTimeFormat={dateTimeFormat}
                    id={item.id}
                    getObjectLink={getObjectLink}
                    onTitleLinkClick={onTitleLinkClick}
                    draggable={draggable}
                    waiting={
                      keyframesUpdate
                        ? keyframesUpdate[item.id]?.loading
                        : undefined
                    }
                    keyframesUpdate={
                      keyframesUpdate
                        ? keyframesUpdate[item.id]?.objects
                        : undefined
                    }
                    dragAndDropVSide={
                      dropTargetId === item.id ? dragAndDropVSide : undefined
                    }
                  />
                </ErrorBoundary>
              ))}
            </tbody>
          </table>
          {isLoading && <LoadingBar className={styles.loading_bar} />}
          {!isLoading && onBottomVisible && (
            <InterObserver
              key={`${this.interObserverKeyPrefix}_${items.length}`}
              className={styles.inter_observer}
              onIntersect={onBottomVisible}
            />
          )}
        </div>
      </div>
    );
  }
}

export default TableView;
